import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../firebase.init";

const SelectPackage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user] = useAuthState(auth);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://allinone-clone-7d5d795e98ce.herokuapp.com/packages");
      if (!response.ok) throw new Error("Failed to fetch packages");
      const data = await response.json();
      setPackages(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching packages:", err);
      setError("Unable to load pricing plans at this time.");
    } finally {
      setLoading(false);
    }
  };

  // Optimized Helper to dynamically filter out empty features
  const getPackageFeatures = (pkg) => {
    return [
      pkg.pointOne,
      pkg.pointTwo,
      pkg.pointThree,
      pkg.pointFour,
      pkg.pointFive,
      pkg.pointSix,
      pkg.pointSeven,
      pkg.pointEight,
      pkg.pointNine,
      pkg.pointTen,
    ].filter(Boolean); // Removes null, undefined, or empty strings automatically
  };

  if (loading) {
    return (
      <div className="premium-loader-wrapper">
        <div className="premium-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          /* --- Global Section --- */
          .pricing-section-premium {
            background-color: transparent;
            padding: 100px 0;
            position: relative;
          }

          /* --- Typography --- */
          .pricing-section-title {
            font-size: clamp(2rem, 3vw, 3rem);
            font-weight: 800;
            color: #ffffff;
            letter-spacing: -1px;
            margin-bottom: 16px;
          }
          .pricing-section-desc {
            color: #A1A1AA;
            font-size: 1.125rem;
            max-width: 600px;
            margin: 0 auto 60px auto;
          }

          /* --- Pricing Cards --- */
          .pricing-card {
            background: rgba(20, 20, 20, 0.6);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            border: 1px solid rgba(255, 255, 255, 0.08);
            border-radius: 24px;
            padding: 40px;
            display: flex;
            flex-direction: column;
            height: 100%;
            position: relative;
            transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4);
            z-index: 1;
            color: #ffffff;
          }
          
          .pricing-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
            border-color: rgba(255, 255, 255, 0.15);
          }

          /* Highlighted "Popular" Card */
          .pricing-card.popular {
            border: 1px solid var(--accent-primary);
            box-shadow: 0 0 30px rgba(99, 102, 241, 0.2);
            transform: scale(1.02);
            z-index: 2;
            background: rgba(30, 30, 30, 0.8);
          }
          .pricing-card.popular:hover {
            transform: scale(1.02) translateY(-8px);
            box-shadow: 0 0 40px rgba(99, 102, 241, 0.3);
          }

          .popular-badge {
            position: absolute;
            top: -14px;
            left: 50%;
            transform: translateX(-50%);
            background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          }

          /* --- Card Header & Price --- */
          .pkg-name {
            font-size: 1.25rem;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 16px;
          }
          .pkg-price-wrapper {
            margin-bottom: 32px;
            display: flex;
            align-items: baseline;
          }
          .pkg-price {
            font-size: 3.5rem;
            font-weight: 800;
            color: #ffffff;
            line-height: 1;
            letter-spacing: -2px;
          }
          .pkg-currency {
            font-size: 1.5rem;
            font-weight: 600;
            color: #A1A1AA;
            margin-right: 4px;
          }
          .pkg-period {
            color: #A1A1AA;
            font-weight: 500;
            margin-left: 8px;
          }

          .card-divider {
            height: 1px;
            background: rgba(255, 255, 255, 0.1);
            margin-bottom: 32px;
            width: 100%;
          }

          /* --- Features List --- */
          .features-list {
            list-style: none;
            padding: 0;
            margin: 0 0 40px 0;
            flex-grow: 1;
          }
          .feature-item {
            display: flex;
            align-items: flex-start;
            margin-bottom: 16px;
            color: #A1A1AA;
            font-size: 1rem;
            line-height: 1.5;
          }
          .feature-icon-check {
            flex-shrink: 0;
            width: 20px;
            height: 20px;
            margin-right: 12px;
            margin-top: 2px;
            color: #10b981; /* Emerald green */
          }
          .popular .feature-icon-check {
            color: var(--accent-primary);
          }

          /* --- Buttons --- */
          .btn-pricing {
            display: block;
            width: 100%;
            padding: 14px 24px;
            border-radius: 12px;
            font-weight: 600;
            font-size: 1rem;
            text-align: center;
            text-decoration: none;
            transition: all 0.3s ease;
            margin-top: auto; /* Pushes button to the bottom */
          }
          
          .btn-outline {
            background: transparent;
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          .btn-outline:hover {
            background: rgba(255, 255, 255, 0.05);
            border-color: rgba(255, 255, 255, 0.3);
            color: #ffffff;
          }

          .btn-solid {
            background: var(--accent-primary);
            color: #ffffff;
            border: 1px solid var(--accent-primary);
            box-shadow: 0 4px 14px 0 rgba(99, 102, 241, 0.39);
          }
          .btn-solid:hover {
            background: var(--accent-primary-hover);
            border-color: var(--accent-primary-hover);
            color: #ffffff;
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.23);
          }

          /* --- Loader --- */
          .premium-loader-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 50vh;
          }
          .premium-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(99, 102, 241, 0.1);
            border-radius: 50%;
            border-top-color: var(--accent-primary);
            animation: spin 1s ease-in-out infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
          
          /* Responsive fixes */
          @media (max-width: 1199px) {
            .pricing-card.popular { transform: none; }
            .pricing-card.popular:hover { transform: translateY(-8px); }
          }
        `}
      </style>

      <section id="pricing" className="pricing-section-premium">
        <div className="container">
          {/* Optional: Section Titles (Looks much better with a header) */}
          <div className="text-center">
            <h2 className="pricing-section-title">
              Simple, transparent pricing
            </h2>
            <p className="pricing-section-desc">
              Choose the perfect plan for your business needs. No hidden fees,
              cancel anytime.
            </p>
          </div>

          {error && (
            <div
              className="alert alert-danger text-center max-w-md mx-auto rounded-3 border-0 shadow-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          {!error && (
            <div className="row g-4 justify-content-center align-items-center mt-4">
              {packages.map((pkg, index) => {
                const features = getPackageFeatures(pkg);
                // Standard UI practice: if there are 3 tiers, highlight the middle one (index 1)
                const isPopular = index === 1;

                return (
                  <div
                    key={pkg._id || index}
                    className="col-12 col-md-6 col-lg-4 wow fadeInUp"
                    data-wow-delay={`${index * 0.15}s`}
                  >
                    <div
                      className={`pricing-card ${isPopular ? "popular" : ""}`}
                    >
                      {isPopular && (
                        <div className="popular-badge">Most Popular</div>
                      )}

                      <h4 className="pkg-name">
                        {pkg.packageName || "Standard Plan"}
                      </h4>

                      <div className="pkg-price-wrapper">
                        <span className="pkg-currency">$</span>
                        <span className="pkg-price">{pkg.price || "0"}</span>
                        <span className="pkg-period">USD</span>
                      </div>

                      <div className="card-divider"></div>

                      <ul className="features-list">
                        {features.length > 0 ? (
                          features.map((feature, idx) => (
                            <li key={idx} className="feature-item">
                              {/* Premium crisp inline SVG Checkmark */}
                              <svg
                                className="feature-icon-check"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span>{feature}</span>
                            </li>
                          ))
                        ) : (
                          <li className="feature-item text-muted">
                            <span>No features listed</span>
                          </li>
                        )}
                      </ul>

                      {user ? (
                        <Link
                          to={`/buy/${pkg._id}`}
                          className={`btn-pricing ${isPopular ? "btn-solid" : "btn-outline"}`}
                        >
                          Buy Now
                        </Link>
                      ) : (
                        <Link
                          to={`/register`}
                          state={{ from: { pathname: `/buy/${pkg._id}` } }}
                          className={`btn-pricing ${isPopular ? "btn-solid" : "btn-outline"}`}
                        >
                          Buy Now
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default SelectPackage;
